import './MappedAttribute.scss';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FlightButton, FlightTooltip, getIcon } from '@flybits/design-system';
import { ReactComponent as IconErrorTriangle } from 'assets/icons/icon-error-triangle.svg';

const MAIN_CLASS = 'mapped-attribute';
const CLASSES = {
  CONTAINER: `${MAIN_CLASS}__container`,
  ATTRIBUTE: `${MAIN_CLASS}__container__attribute`,
  NAME: `${MAIN_CLASS}__container__attribute__name`,
  NAME_TOOLTIP: `${MAIN_CLASS}__container__attribute__name-tooltip`,
  ERROR: `${MAIN_CLASS}__container__attribute__error`,
  WARNING: `${MAIN_CLASS}__container__attribute__error--warning`,
  EDIT: `${MAIN_CLASS}__container__edit`,
  ADD: `${MAIN_CLASS}__container__add`,
};

type TMappedAttributeProps = {
  value: string;
  isError?: boolean;
  errorMessage?: string;
  errorType?: 'error' | 'warning' | null;
  onEdit?: (() => void) | null;
  onAdd?: () => void;
};

function MappedAttribute(props: TMappedAttributeProps) {
  const { value, isError = false, errorMessage = '', errorType, onEdit, onAdd } = props;

  const [isNameOverflowing, setNameOverflowing] = useState(false);
  const nameRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!value) {
      return;
    }

    if (nameRef.current) {
      const isOverflow = nameRef.current.scrollWidth > nameRef.current.clientWidth;
      setNameOverflowing(isOverflow);
    }
  }, [value]);

  const handleClickEdit = useCallback(() => {
    if (onEdit) {
      onEdit();
    }
  }, [onEdit]);

  const handleClickAdd = useCallback(() => {
    if (onAdd) {
      onAdd();
    }
  }, [onAdd]);

  return (
    <div className={MAIN_CLASS}>
      <div className={CLASSES.CONTAINER}>
        {value ? (
          <>
            <div className={CLASSES.ATTRIBUTE}>
              <FlightTooltip
                className={CLASSES.NAME_TOOLTIP}
                isEnabled={isNameOverflowing}
                description={value}
                direction="bottom"
              >
                <div className={CLASSES.NAME} ref={nameRef}>
                  {value}
                </div>
              </FlightTooltip>
              {isError && (
                <FlightTooltip isEnabled={isError} description={errorMessage} direction="bottom">
                  <div className={`${CLASSES.ERROR} ${errorType === 'warning' ? CLASSES.WARNING : ''}`}>
                    <IconErrorTriangle />
                  </div>
                </FlightTooltip>
              )}
            </div>
            {onEdit && (
              <div className={CLASSES.EDIT}>
                <button onClick={handleClickEdit} aria-label="Edit attribute mapping">
                  {getIcon('editFullOutline', {})}
                </button>
              </div>
            )}
          </>
        ) : (
          <FlightButton
            className={CLASSES.ADD}
            theme={'link'}
            size={'large'}
            iconLeft={'add'}
            label={'Add context plugin'}
            onClick={handleClickAdd}
          />
        )}
      </div>
    </div>
  );
}

export default MappedAttribute;
